/**
 * Shared Highcharts configuration helpers.
 *
 * Import these constants instead of repeating hard-coded hex values across
 * chart components. All colour references use CSS custom properties so they
 * automatically adapt to light / dark theme switches.
 */

/** Legend item-style block shared by every chart. */
export const HC_LEGEND_STYLE = {
  itemStyle: {
    color: 'var(--text-dim)',
    fontFamily: 'Inter, system-ui, sans-serif',
    fontWeight: '500',
    fontSize: '11.5px',
  },
  itemHoverStyle: { color: 'var(--text)' },
  itemHiddenStyle: { color: 'var(--text-faint)' },
} as const;

/** Standard crosshair used on time-series xAxis. */
export const HC_CROSSHAIR_STYLE = {
  color: 'var(--accent-ring)',
  width: 1,
  dashStyle: 'ShortDot' as Highcharts.DashStyleValue,
};

/**
 * Shared tooltip styling. Sets `backgroundColor`/`borderColor`/`fontFamily`
 * explicitly so SVG and `useHTML: true` tooltips render identically in both
 * themes — HTML tooltips render the formatter into an inline-styled `<span>`
 * whose `font-family` comes from `tooltip.style`, bypassing the SVG-only
 * `.highcharts-tooltip text` CSS rule.
 */
export const HC_TOOLTIP_STYLE = {
  outside: false,
  backgroundColor: 'var(--surface-2)',
  borderColor: 'var(--border-strong)',
  borderRadius: 8,
  borderWidth: 1,
  padding: 10,
  shadow: false,
  style: {
    color: 'var(--text)',
    fontFamily: 'var(--font-ui)',
    fontSize: '12px',
  },
} as const;

/**
 * Shared range-selector (zoom) styling for stock charts. Buttons are aligned
 * to the right and use the design-token "pill" theme — the larger `padding`/`r`
 * box is what makes these buttons match the cumulative-return chart size
 * (CSS alone cannot set the SVG button-box geometry). Spread into a chart's
 * `rangeSelector` alongside its own `buttons` / `selected`:
 *
 *   rangeSelector: { ...HC_RANGE_SELECTOR, buttons, selected: buttons.length - 1 }
 */
export const HC_RANGE_SELECTOR: Highcharts.RangeSelectorOptions = {
  inputEnabled: false,
  buttonSpacing: 6,
  buttonPosition: { align: 'right' },
  buttonTheme: {
    padding: 5,
    r: 20,
    fill: 'var(--surface-2)',
    stroke: 'var(--border)',
    'stroke-width': 1,
    style: {
      color: 'var(--text-dim)',
      fontWeight: '500',
      fontSize: '11.5px',
      fontFamily: 'Inter, system-ui, sans-serif',
    },
    states: {
      hover: {
        fill: 'var(--surface-2)',
        stroke: 'var(--border-strong)',
        style: { color: 'var(--text)' },
      },
      select: {
        fill: 'var(--accent-soft)',
        stroke: 'rgba(124, 92, 255, 0.35)',
        style: { color: 'var(--accent-2)', fontWeight: '600' },
      },
      disabled: { style: { color: 'var(--text-faint)' } },
    },
  } as any,
  labelStyle: { display: 'none' },
};

/**
 * Named design-token colour references for Highcharts JS config
 * (e.g. `addPoint`, `series` options) where CSS styled-mode alone is not
 * sufficient because the value must be passed as a JS string.
 */
export const HC_COLORS = {
  accent:    'var(--accent)',       // #7C5CFF
  accent2:   'var(--accent-2)',     // #A78BFF
  accentRing: 'var(--accent-ring)', // rgba(124,92,255,0.35)
  accentSoft: 'var(--accent-soft)', // rgba(124,92,255,0.12)
  pos:       'var(--pos)',          // #00E5A0
  neg:       'var(--neg)',          // #FF5C7A
  warn:      'var(--warn)',         // #FFB547
  info:      'var(--info)',         // #54C7FF
  text:      'var(--text)',
  textDim:   'var(--text-dim)',
  textFaint: 'var(--text-faint)',
} as const;

/**
 * Raw hex values for the primary design-token colours.
 * Use these ONLY when a JavaScript API (e.g. Highcharts.color(), shadeColor())
 * needs to manipulate the colour mathematically. Prefer HC_COLORS everywhere else.
 */
export const HC_HEX = {
  accent:  '#7C5CFF',
  accent2: '#A78BFF',
  pos:     '#00E5A0',
  neg:     '#FF5C7A',
  warn:    '#FFB547',
  info:    '#54C7FF',
} as const;
